import * as LDSDK from 'launchdarkly-js-client-sdk'
import {
  AppWithPorts,
  ElmTaggedType,
  PortFromElm,
  PortToElm,
} from 'DTSLib/MPArchitectureFoundations/types'
import { DynamicFlagsFetchingMode } from '../../../project-conf'
import { LDClient } from 'launchdarkly-js-client-sdk'
import Logger from 'DTSLib/Logger'

export type ConstructorArgs = {
  clientId: string
  /**
   *  Use Website User Unique Identifier (wu_uid) or another consistent value as key property alongside other data you may want to add
   */
  context: LDSDK.LDContext
  flagsFetchingMode: DynamicFlagsFetchingMode
  /**
   * Enables explicit hit logging in the console.
   * This is used as a helper for analytics teams to observe the event payloads being sent,
   * allowing them to inspect the structure and optionally configure KPIs on their platforms.
   */
  logHitSending: boolean

  /**
   * Global logger instance (used to properly log errors or debugging information on local/staging environment)
   */
  logger: Logger
}
/**
 * A generic event type supported by LD tracking API,
 * LD API doesn't provide a native set of high-level events, so we provide them below using this type
 */
export type Event = {
  data?: unknown
  key: string
  metrics?: number
}

/**
 * Utility builder for standard action tracking event
 *
 * @param actionId - the action (unique?) identifier you want to track
 * @param label - optionally a label which may further identify your action
 * @param value - optionally a value number which may further identify your action
 * @returns an Event type which tracks the page view information
 */
export function actionEvent(actionId: string, label?: string, value?: number): Event {
  return { data: { label, value }, key: actionId }
}

export class LaunchDarkly {
  private client: LDClient

  private readonly logHitSending: boolean

  private readonly logger: Logger

  private readonly flagsFetchingMode: DynamicFlagsFetchingMode

  /**
   * The session storage key used to initial fetch flags in 'local' mode
   *
   * In this store the expected shape of the flags are :
   * \{ 'flag-name-1': 'flag1-serialized-value'
   *  , 'flag-name-2': 'flag2-serialized-value'
   * \}
   */
  private static readonly sessionStorageKey = 'LaunchDarkly.SimulatedFlagsStore'

  /**
   * The session storage key used to trigger flags changes in 'local' mode
   *
   * In this store the expected for of the 'update' is in the form:
   * \{ 'flag1-name': \{ 'previous': 'flag1-serialized-value', 'current':  'flag1-serialized-value' \}
   *  , 'flag2-name': \{ 'previous': 'flag2-serialized-value', 'current': 'flag2-serialized-value' \}
   * \}
   */
  private static readonly sessionStorageChangeKey = 'LaunchDarkly.SimulatedUpdatesStore'

  /**
   * Wraps flagship start method in a class constructor which ensure to initialize a visitor
   *
   * @param args - constructor argument record
   */
  public constructor(args: ConstructorArgs) {
    this.client = LDSDK.initialize(args.clientId, args.context, {
      plugins: [],
    })
    this.logHitSending = args.logHitSending || false
    this.logger = args.logger
    this.flagsFetchingMode = args.flagsFetchingMode
  }

  /**
   * Fetch the flag from LaunchDarkly. Wraps the stuff into an async promise due to initial fetchFlags phase.
   *
   * @param flagName - a flag name eg: "button-color"
   * @param fallbackValue - a fallback error returned in case of error eg: "red"
   * @returns a T value wrapped into flagship IFlag type
   */
  public async getFlag<T>(flagName: string, fallbackValue: T): Promise<T> {
    switch (this.flagsFetchingMode) {
      case DynamicFlagsFetchingMode.disabled:
        return fallbackValue
      case DynamicFlagsFetchingMode.local: {
        const stringifiedVal =
          window.sessionStorage.getItem(LaunchDarkly.sessionStorageKey) || JSON.stringify({})

        const sessionStorageObject = JSON.parse(stringifiedVal)
        if (Object.prototype.hasOwnProperty.call(sessionStorageObject, flagName)) {
          return sessionStorageObject[flagName] as T
        } else {
          return fallbackValue
        }
      }
      case DynamicFlagsFetchingMode.remote: {
        return this.getFlagFromLaunchDarkly(flagName, fallbackValue)
      }

      default: {
        this.logger.devConsoleError(
          `LaunchDarkly:unrecognized flags fetching mode value: ${this.flagsFetchingMode}`
        )

        return fallbackValue
      }
    }
  }

  /**
   * Fetch the flag from LaunchDarkly. Wraps the stuff into an async promise due to initial fetchFlags phase.
   *
   * @param flagName - a flag name eg: "button-color"
   * @param fallbackValue - a fallback error returned in case of error eg: "red"
   * @returns a T value wrapped into flagship IFlag type
   */
  private async getFlagFromLaunchDarkly<T>(flagName: string, fallbackValue: T): Promise<T> {
    try {
      await this.client.waitForInitialization(5)
      const variation = this.client.variation(flagName, fallbackValue) as T
      // We force flushing to ensure events are correctly sent
      await this.client.flush()

      return variation
    } catch (e) {
      this.logger.devConsoleLog('LaunchDarkly:getFlag', e)

      return fallbackValue
    }
  }

  /**
   * Wraps internal LD identify into a promise. Once resolved a new flag set is ready to be used
   *
   * @param context -  the new context
   * @param hash - The signed context key if you are using [Secure Mode](https://docs.launchdarkly.com/sdk/features/secure-mode#configuring-secure-mode-in-the-javascript-client-side-sdk).
   */
  public async updateContext(context: LDSDK.LDContext, hash?: string): Promise<void> {
    this.logger.devConsoleLog('LaunchDarkly:updatedContext', context)

    // We contact launchdarkly only in remote-mode to avoid to be blocked and trigger unwanted errors in testing or other envs
    if (this.flagsFetchingMode == DynamicFlagsFetchingMode.remote) {
      try {
        await this.client.waitForInitialization(5)

        await this.client.identify(context, hash)
        await this.client.flush()
      } catch (e) {
        this.logger.devConsoleLog('LaunchDarkly:updateContext', e)
      }
    }
  }

  /**
   * Wraps internal LD send event api
   *
   * @param event - event you want to track
   */
  public async trackEvent(event: Event): Promise<void> {
    try {
      await this.client.waitForInitialization(5)
      if (this.logHitSending) {
        console.log('LaunchDarkly:trackEvent', event)
      }

      this.client.track(event.key, event.data, event.metrics)
    } catch (e) {
      this.logger.devConsoleLog('LaunchDarkly:trackEvent', e)
    }
  }

  /**
   * Batch track a list of events
   *
   * @param events -  a list of events
   */
  public async trackEvents(events: Event[]): Promise<void> {
    if (this.logHitSending) {
      console.log('LaunchDarkly:trackEvents ...')
    }

    for (let i = 0; i < events.length; i += 1) {
      await this.trackEvent(events[i])
    }
  }

  /**
   * Mount elm App ports
   *
   * @param app - The App which implements FlagshipIO module
   */
  public mount(app: AppWithPorts<LaunchDarklyPortInterface>): void {
    app.ports.launchDarklyPort_.subscribe((portValue) => {
      switch (portValue.type_) {
        case 'track_event': {
          this.trackEvent(portValue.event)
            .then()
            .catch((err) => {
              this.logger.devConsoleLog('LaunchDarkly:launchDarklyPort_ track_event', err)
            })
          break
        }
        case 'track_events': {
          this.trackEvents(portValue.events)
            .then()
            .catch((err) => {
              this.logger.devConsoleLog('LaunchDarkly:launchDarklyPort_ track_events', err)
            })
          break
        }
        case 'update_context': {
          this.updateContext(portValue.context)
            .then()
            .catch((err) => {
              this.logger.devConsoleLog('LaunchDarkly:launchDarklyPort_ update_context', err)
            })
          break
        }

        default: {
          this.logger.devConsoleError(
            'LaunchDarkly:launchDarklyPort_ unrecognized portValue type',
            portValue
          )
          break
        }
      }
    })

    switch (this.flagsFetchingMode) {
      case DynamicFlagsFetchingMode.remote: {
        // The settings object contains a map of updated feature flag keys and values.
        // The map only contains the keys to flags that have changed
        // Flag re-evaluation may happen after a context update or after remote flag values are changed (in case of stream connection setting)
        this.client.on('change', (flagsMap) => {
          this.logger.devConsoleLog('LaunchDarkly:change flags', flagsMap)
          this.client.flush().finally(() => {
            if (typeof app.ports.launchDarklySubscription_ !== 'undefined') {
              app.ports.launchDarklySubscription_.send({
                flagsMap,
                type_: 'flags_changed',
              })
            } else {
              this.logger.devConsoleLog(
                'LaunchDarkly:change flags',
                'launchDarklySubscription_ port is not present, Elm app have not been live-updated'
              )
            }
          })
        })
        break
      }
      case DynamicFlagsFetchingMode.local: {
        // Since we want to be able to test locally the launchdarkly side update of a flag
        // we polyfill the setItem method of session storage and whenever the sessionStorageChangeKey is changed
        // we trigger an update through the Elm port
        const originalCallBack = Storage.prototype.setItem
        const keyToCheck = LaunchDarkly.sessionStorageChangeKey
        Storage.prototype.setItem = function (key, value): void {
          originalCallBack.apply(this, [key, value])
          if (key == keyToCheck && typeof app.ports.launchDarklySubscription_ !== 'undefined') {
            app.ports.launchDarklySubscription_.send({
              flagsMap: JSON.parse(value),
              type_: 'flags_changed',
            })
          }
        }
        break
      }

      default: {
        // Do nothing
        break
      }
    }
  }
}

/** Union of all port messages handled by the LaunchDarkly port. */
export type LaunchDarklyPortValue = TrackEvent | TrackEvents | UpdateContext

export type LaunchDarklySubscriptionValue = FlagsChanged

export interface FlagsChanged extends ElmTaggedType {
  flagsMap: unknown
  type_: 'flags_changed'
}

/** Represents a single tracking hit (event or pageview) sent via Elm. */
export interface TrackEvent extends ElmTaggedType {
  event: Event
  type_: 'track_event'
}

/** Represents multiple tracking hits batched together. */
export interface TrackEvents extends ElmTaggedType {
  events: Event[]
  type_: 'track_events'
}

/** Represents an update to the visitor's context from Elm. */
export interface UpdateContext extends ElmTaggedType {
  context: LDSDK.LDContext
  type_: 'update_context'
}

/** Defines the Elm port interface for the LaunchDarkly.ts module. */
export interface LaunchDarklyPortInterface {
  launchDarklyPort_: PortFromElm<LaunchDarklyPortValue>
  // We declare the subscription as possibly undefined because we may not want to use the 'change while running' feature
  launchDarklySubscription_?: PortToElm<LaunchDarklySubscriptionValue>
}
